/**
 * Depreciation Config API Client
 *
 * Implements EntityClient<DepreciationProductRecord, DepreciationProductUpdatePayload,
 * DepreciationProductCreatePayload> against /api/v1/insurance/depreciation/products,
 * matching the interface shape from lib/rate-master/v2-api.ts so
 * hooks/useDepreciation.ts can build on the shared createEntityHooks()
 * factory (see hooks/useRateMasterV2Entity.ts), plus standalone functions
 * for the shared lookups and the combined rates grid.
 */

import type { EntityClient } from '@/lib/rate-master/v2-api';
import type { ImportResponse, ListResponse } from '@/types/rate-master-v2';
import type {
  AgeBandListResponse,
  DepreciationProductCreatePayload,
  DepreciationProductListResponse,
  DepreciationProductRecord,
  DepreciationProductUpdatePayload,
  DepreciationRatesResponse,
  DepreciationRatesUpdatePayload,
  PartCategoryCreatePayload,
  PartCategoryListResponse,
  PartCategoryRecord,
} from '@/types/depreciation';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

const BASE = `${API_URL}/api/v1/insurance/depreciation`;
const PRODUCTS_BASE = `${BASE}/products`;
const PART_CATEGORIES_BASE = `${BASE}/part-categories`;
const AGE_BANDS_BASE = `${BASE}/age-bands`;
const RATES_BASE = `${BASE}/rates`;

// ============================================================================
// AUTH
// ============================================================================

function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  return (
    localStorage.getItem('super_admin_token') ||
    localStorage.getItem('access_token')
  );
}

function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// ============================================================================
// ERROR HANDLING
// ============================================================================

async function parseErrorMessage(response: Response): Promise<string> {
  const defaultMessage = `Request failed with status ${response.status}`;
  try {
    const body = await response.json();
    const detail = body?.detail ?? body?.error?.message;
    if (!detail) return defaultMessage;
    return typeof detail === 'string' ? detail : JSON.stringify(detail);
  } catch {
    return defaultMessage;
  }
}

async function assertOk(response: Response): Promise<void> {
  if (response.ok) return;
  const message = await parseErrorMessage(response);
  throw new Error(message);
}

async function fetchJson<T>(response: Response): Promise<T> {
  await assertOk(response);
  return response.json() as Promise<T>;
}

function buildQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      qs.set(key, String(value));
    }
  }
  return qs.toString();
}

// ============================================================================
// ENTITY CLIENT — products
// ============================================================================

/**
 * EntityClient implementation for Depreciation products. `get` and
 * `importFile` satisfy the shared interface but are unused by the current
 * UI (no detail view, no bulk import for this module).
 */
export const depreciationClient: EntityClient<
  DepreciationProductRecord,
  DepreciationProductUpdatePayload,
  DepreciationProductCreatePayload
> = {
  async list(params = {}): Promise<ListResponse<DepreciationProductRecord>> {
    const query = buildQueryString(params);
    const url = query ? `${PRODUCTS_BASE}?${query}` : PRODUCTS_BASE;
    const response = await fetch(url, { method: 'GET', headers: authHeaders() });
    const body = await fetchJson<DepreciationProductListResponse>(response);
    return {
      items: body.items,
      total: body.total,
      limit: typeof params.limit === 'number' ? params.limit : body.items.length,
      offset: typeof params.offset === 'number' ? params.offset : 0,
    };
  },

  async get(id: string): Promise<DepreciationProductRecord> {
    const response = await fetch(`${PRODUCTS_BASE}/${id}`, {
      method: 'GET',
      headers: authHeaders(),
    });
    return fetchJson<DepreciationProductRecord>(response);
  },

  async create(payload: DepreciationProductCreatePayload): Promise<DepreciationProductRecord> {
    const response = await fetch(PRODUCTS_BASE, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    return fetchJson<DepreciationProductRecord>(response);
  },

  async update(id: string, payload: DepreciationProductUpdatePayload): Promise<DepreciationProductRecord> {
    const response = await fetch(`${PRODUCTS_BASE}/${id}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    return fetchJson<DepreciationProductRecord>(response);
  },

  async remove(): Promise<void> {
    throw new Error('Deleting depreciation products is not supported.');
  },

  async importFile(): Promise<ImportResponse> {
    throw new Error('Bulk import is not supported for Depreciation products.');
  },
};

// ============================================================================
// SHARED LOOKUPS
// ============================================================================

export async function getPartCategories(): Promise<PartCategoryListResponse> {
  const response = await fetch(PART_CATEGORIES_BASE, { method: 'GET', headers: authHeaders() });
  return fetchJson<PartCategoryListResponse>(response);
}

export async function createPartCategory(payload: PartCategoryCreatePayload): Promise<PartCategoryRecord> {
  const response = await fetch(PART_CATEGORIES_BASE, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return fetchJson<PartCategoryRecord>(response);
}

export async function getAgeBands(): Promise<AgeBandListResponse> {
  const response = await fetch(AGE_BANDS_BASE, { method: 'GET', headers: authHeaders() });
  return fetchJson<AgeBandListResponse>(response);
}

// ============================================================================
// RATES GRID
// ============================================================================

export async function getRates(productId: string): Promise<DepreciationRatesResponse> {
  const response = await fetch(`${RATES_BASE}?product_id=${productId}`, {
    method: 'GET',
    headers: authHeaders(),
  });
  return fetchJson<DepreciationRatesResponse>(response);
}

export async function saveRates(
  productId: string,
  payload: DepreciationRatesUpdatePayload,
): Promise<DepreciationRatesResponse> {
  const response = await fetch(`${RATES_BASE}?product_id=${productId}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return fetchJson<DepreciationRatesResponse>(response);
}
